<template>
  <div class="wt2-sg">
    <button
      type="button"
      class="wpromo-pill"
      aria-label="Get the Wochit App"
      @click="click"
    >
      <span class="wpromo-spark">
        <svg class="wpromo-star -lg" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 0c.6 5.4 2.2 7 7 7-4.8 0-6.4 1.6-7 7-.6-5.4-2.2-7-7-7 4.8 0 6.4-1.6 7-7z"
          />
        </svg>
        <svg class="wpromo-star -main" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 0c.6 5.4 2.2 7 7 7-4.8 0-6.4 1.6-7 7-.6-5.4-2.2-7-7-7 4.8 0 6.4-1.6 7-7z"
          />
        </svg>
        <svg class="wpromo-star -sm" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 0c.6 5.4 2.2 7 7 7-4.8 0-6.4 1.6-7 7-.6-5.4-2.2-7-7-7 4.8 0 6.4-1.6 7-7z"
          />
        </svg>
      </span>
      <span>Get the Wochit App</span>
    </button>
  </div>
</template>

<script>
import app from 'app';
import wtGetTheApp from 'components/modals/types/wtGetTheApp.component.vue';

export default {
  name: 'GetTheAppButton',

  methods: {
    click() {
      app.analytics('masthead:getTheApp', { source: 'button' });

      app.showModal({
        type: 'custom',
        component: wtGetTheApp,
        closeOnClickOut: true,
        whiteCloseButton: true,
        wrapperClass: ['wt-get-the-app-modal'],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/* Sweep the sheen left-to-right across the pill, then hold it parked
   off-screen for the rest of the cycle. Driven as a moving background layer
   (see .wpromo-pill) so it can never overflow the rounded box. */
@keyframes wpromoShine {
  0% {
    background-position: 220% 0;
  }
  55%,
  100% {
    background-position: -100% 0;
  }
}
@keyframes wpromoBorder {
  0%,
  100% {
    border-color: rgba(0, 174, 239, 0.55);
  }
  50% {
    border-color: rgba(0, 174, 239, 1);
  }
}
/* Each star keeps its own static rotation (--r) and twinkles in place.
   Staggered animation-delays make the three sparkle out of sync. */
@keyframes wpromoSpark {
  0%,
  100% {
    transform: rotate(var(--r, 0deg)) scale(1);
    opacity: var(--o, 0.9);
  }
  50% {
    transform: rotate(var(--r, 0deg)) scale(var(--s, 1.18));
    opacity: 1;
  }
}

// The root carries the .wt2-sg design-system class (like the "Make a Wochit"
// button). That context also applies `.wt2-sg button { height:50px; padding:0
// 20px; border:none; border-radius:0 }`, which would flatten the pill and kill
// its animated border — so every pill rule is scoped under .wt2-sg to
// out-specify it (0,2,0 > 0,1,1).
.wt2-sg {
  // The dashboard masthead forces `.wt-header-actions div { height:100% }`, so
  // this wrapper is stretched to the full masthead height. Center the pill
  // inside it instead of letting it sit at the top.
  display: flex;
  align-items: center;
  // breathing room before the "Make a Wochit" button
  margin-right: 16px;
}

.wt2-sg .wpromo-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 15px;
  border: 1.5px solid rgba(0, 174, 239, 0.7);
  border-radius: 16px;
  cursor: pointer;
  vertical-align: middle;
  font: 600 12.5px/1 -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #4fc3f0;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  // The sheen is a moving background layer, not a child element, so it is
  // always clipped to the pill's rounded box and can never overflow the edges.
  background-color: rgba(0, 174, 239, 0.06);
  background-image: linear-gradient(
    105deg,
    transparent 40%,
    rgba(120, 210, 245, 0.4) 50%,
    transparent 60%
  );
  background-repeat: no-repeat;
  background-size: 220% 100%;
  animation: wpromoBorder 3s ease-in-out infinite,
    wpromoShine 3.6s ease-in-out infinite;
  transition: background-color 0.15s ease, transform 0.15s ease,
    color 0.15s ease;
}
.wt2-sg .wpromo-pill:hover {
  // only the base color changes on hover; keep the sheen gradient layer intact
  background-color: rgba(0, 174, 239, 0.16);
  color: #7bd3f5;
  transform: translateY(-1px);
}
.wt2-sg .wpromo-pill:focus-visible {
  outline: 2px solid #4fc3f0;
  outline-offset: 2px;
}
.wt2-sg .wpromo-pill .wpromo-spark {
  position: relative;
  display: inline-flex;
  color: #4fc3f0;
  // nudge the whole cluster right, closer to the text
  transform: translateX(4px);
}
// Each star twinkles on its own duration (--r rotation, --s peak scale,
// --o resting opacity), so the three sparkle at different rhythms and sizes.
.wt2-sg .wpromo-pill .wpromo-star {
  display: block;
  fill: currentColor;
  transform-origin: center;
  animation: wpromoSpark 2.2s ease-in-out infinite;
}
// main star: in flow, defines the cluster footprint and the gap to the text
.wt2-sg .wpromo-pill .wpromo-star.-main {
  position: static;
  width: 12px;
  height: 12px;
  --r: 0deg;
  --o: 0.95;
  --s: 1.16;
  animation-duration: 2.4s;
}
// larger star, up and to the left of the main
.wt2-sg .wpromo-pill .wpromo-star.-lg {
  position: absolute;
  width: 18px;
  height: 18px;
  left: -9px;
  top: 2px;
  --r: -15deg;
  --o: 0.95;
  --s: 1.22;
  animation-duration: 3.1s;
  animation-delay: 0.5s;
}
// smaller star, below the main
.wt2-sg .wpromo-pill .wpromo-star.-sm {
  position: absolute;
  width: 7px;
  height: 7px;
  left: 3px;
  top: 8px;
  --r: 20deg;
  --o: 0.8;
  --s: 1.35;
  animation-duration: 1.7s;
  animation-delay: 0.9s;
}
</style>
